import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Reaction Sport — FTUE demo (3 essais progressive intro).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'reaction_sport.demo.step1_title',
        hintKey: 'reaction_sport.demo.step1_hint',
        mode: 'simple',
        preSignalMs: 2200,
        signalColor: 'green',
    },
    {
        idx: 2,
        titleKey: 'reaction_sport.demo.step2_title',
        hintKey: 'reaction_sport.demo.step2_hint',
        mode: 'simple',
        preSignalMs: 1400,
        signalColor: 'green',
    },
    {
        idx: 3,
        titleKey: 'reaction_sport.demo.step3_title',
        hintKey: 'reaction_sport.demo.step3_hint',
        mode: 'choice2',
        preSignalMs: 1800,
        signalColor: 'red',
    },
];
const FIELD = 240;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('fixation');
    const [outcome, setOutcome] = useState(null);
    const [rtMs, setRtMs] = useState(null);
    const signalStartRef = useRef(0);
    const fixationTimerRef = useRef(null);
    const preSignalTimerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    useEffect(() => {
        if (phase === 'fixation') {
            fixationTimerRef.current = setTimeout(() => setPhase('waiting_signal'), 400);
        }
        else if (phase === 'waiting_signal') {
            preSignalTimerRef.current = setTimeout(() => {
                signalStartRef.current = Date.now();
                setPhase('signal');
            }, step.preSignalMs);
        }
        return () => {
            if (fixationTimerRef.current !== null)
                clearTimeout(fixationTimerRef.current);
            if (preSignalTimerRef.current !== null)
                clearTimeout(preSignalTimerRef.current);
        };
    }, [phase, step]);
    const settle = (kind, rt) => {
        setOutcome(kind);
        setRtMs(rt);
        setPhase('feedback');
    };
    const handleStageTap = () => {
        if (phase === 'waiting_signal') {
            settle('false_start', null);
            return;
        }
        if (phase === 'signal' && step.mode === 'simple') {
            settle('correct', Date.now() - signalStartRef.current);
        }
    };
    const handlePick = (side) => {
        if (phase === 'waiting_signal') {
            settle('false_start', null);
            return;
        }
        if (phase === 'signal' && step.mode === 'choice2') {
            const rt = Date.now() - signalStartRef.current;
            if (side === step.signalColor) {
                settle('correct', rt);
            }
            else {
                settle('wrong_side', rt);
            }
        }
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('fixation');
        setOutcome(null);
        setRtMs(null);
    };
    const stageLabel = (() => {
        if (phase === 'fixation')
            return '+';
        if (phase === 'waiting_signal')
            return t('reaction_sport.prompts.wait');
        return null;
    })();
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('reaction_sport.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("button", { type: "button", onClick: handleStageTap, disabled: phase === 'fixation' || phase === 'feedback', className: "flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-cyan-network disabled:cursor-not-allowed", style: { width: FIELD, height: FIELD, background: '#1a1a1a' }, children: [stageLabel !== null && (_jsx("span", { className: "font-display text-[18px] text-cyan-surface", children: stageLabel })), phase === 'signal' && step.mode === 'simple' && (_jsx("span", { className: "block rounded-full", style: {
                            width: 130,
                            height: 130,
                            background: 'radial-gradient(circle at 30% 30%, #b9f0a3 0%, #4cd964 60%, #1f7a36 100%)',
                            boxShadow: '0 0 60px rgba(76, 217, 100, 0.45)',
                        }, "aria-hidden": true })), phase === 'signal' && step.mode === 'choice2' && (_jsx("span", { className: "block rounded-full", style: {
                            width: 140,
                            height: 140,
                            background: step.signalColor === 'green'
                                ? 'radial-gradient(circle at 30% 30%, #ffffff 0%, #4cd964 55%, #1a1a1a 110%)'
                                : 'radial-gradient(circle at 30% 30%, #ffffff 0%, #ff453a 55%, #1a1a1a 110%)',
                            boxShadow: step.signalColor === 'green'
                                ? '0 0 60px rgba(76, 217, 100, 0.45)'
                                : '0 0 60px rgba(255, 69, 58, 0.45)',
                        }, "aria-hidden": true })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[40px] font-medium',
                            outcome === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: outcome === 'correct' ? '✓' : '✗' }))] }), phase === 'signal' && step.mode === 'choice2' && (_jsxs("div", { className: "flex w-full max-w-[320px] gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('green'), className: "flex-1 inline-flex h-12 items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('reaction_sport.buttons.green') }), _jsx("button", { type: "button", onClick: () => handlePick('red'), className: "flex-1 inline-flex h-12 items-center justify-center bg-cyan-media font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:opacity-90", children: t('reaction_sport.buttons.red') })] })), phase === 'feedback' && (_jsx("p", { className: [
                    'text-[13px]',
                    outcome === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: outcome === 'correct' && rtMs !== null
                    ? t('reaction_sport.feedback.correct_with_rt', {
                        rt: Math.round(rtMs),
                        tier: t(rtMs <= 250
                            ? 'reaction_sport.tiers.pro'
                            : rtMs <= 400
                                ? 'reaction_sport.tiers.active'
                                : 'reaction_sport.tiers.apprentice'),
                    })
                    : outcome === 'false_start'
                        ? t('reaction_sport.feedback.false_start')
                        : outcome === 'wrong_side'
                            ? t('reaction_sport.feedback.wrong_side')
                            : t('reaction_sport.feedback.late') })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map